import { Box, Grid2, Skeleton, styled, Typography } from "@mui/material";
import { StyledCard } from "./components/StyledComponents";
import Lottie from "react-lottie";
import PinkBlinkingLottie from "../../Components/Lotties/PinkBlinkingLottie.json";
import { useCallback, useEffect, useState } from "react";
import { getDiagnosticsStats } from "../../Api/FleetOverviewApi";
import { getCustomBmsFaultsUrl } from "../Overview/CommonFunctions";
import CustomIcon from "../../Components/CustomIcon";
import { usePermissions } from "../../Context/PermissionContext";
import { FEATURE_CODES } from "../../Static/FeatureMapping";
import { Link } from "react-router-dom";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const severityStyles = {
  critical:       { bg: "#FFF3F3", border: "#B50500", text: "#B50500", actionText: "need action immediately" },
  'high severity':{ bg: "#FFF6F0", border: "#FF6B0F", text: "#FF6B0F", actionText: "need action" },
  warning:        { bg: "#FFF9E7", border: "#DCA101", text: "#DCA101", actionText: "need action" },
  resolved:       { bg: "#F0FFF4", border: "#00B050", text: "#00B050", actionText: "no action required" },
};

const StyledAlertBox = styled(Box)(({ severity }) => {
  const style = severityStyles[severity] || severityStyles.warning;
  return {
    backgroundColor: style.bg,
    borderLeft: `2px solid ${style.border}`,
    padding: "16px 20px",
    display: "flex",
    flexDirection: "column",
  };
});

// Stable lottie config — hoisted to module scope to avoid recreation on every render
const LOTTIE_OPTIONS = {
  loop: true,
  autoplay: true,
  animationData: PinkBlinkingLottie,
  rendererSettings: { preserveAspectRatio: "xMidYMid slice" },
};

/**
 * Custom platform LiveIssueStreamCard.
 * Inherited from the Coulomb version — shows active BMS alert severity breakdown.
 */
const LiveIssueStreamCard = ({ globalFilters, config = {} }) => {
  const [alertStats, setAlertStats] = useState([]);
  const [loading, setLoading] = useState(false);
  const { hasPermission, isRbacV2Enabled } = usePermissions();
  const bmsAlertsEnabled = !isRbacV2Enabled() || hasPermission(FEATURE_CODES.INDIGO_OBSERVABILITY_PLATFORM_BMS_ALERTS, 'view');
  const SHOW_LIVE_ISSUE_STREAM = config.enabled === true && bmsAlertsEnabled;

  // processStats depends on globalFilters (for URL generation) — memoized to
  // avoid unnecessary recreation and to keep fetchStats deps correct.
  const processStats = useCallback((statsData) => {
    if (!statsData || !statsData.activeStats?.severityBreakup) return [];
    const sortBy = [{ sortBy: "customerDBTimeInsert", order: "desc" }];
    const activeAlerts = statsData.activeStats.severityBreakup.map((item) => ({
      severity: item.severityName.toLowerCase(),
      label: item.severityName.toUpperCase(),
      totalCount: item.total,
      actionCount: item.totalBatteriesAffected,
      viewUrl: getCustomBmsFaultsUrl(globalFilters, { severityName: [item.severityName] }, sortBy),
    }));
    const resolved = statsData.resolvedStats
      ? {
          severity: "resolved",
          label: "RESOLVED",
          totalCount: statsData.resolvedStats.totalToday,
          actionCount: statsData.resolvedStats.totalBatteriesAffected || 0,
          viewUrl: getCustomBmsFaultsUrl(globalFilters, {}, sortBy, "resolvedFaults"),
        }
      : null;
    return resolved ? [...activeAlerts, resolved] : activeAlerts;
  }, [globalFilters]);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    const statsResponse = await getDiagnosticsStats(globalFilters, true, 'overview');
    const statsData = statsResponse?.response?.data || {};
    setAlertStats(processStats(statsData));
    setLoading(false);
  }, [globalFilters, processStats]);

  useEffect(() => {
    if (SHOW_LIVE_ISSUE_STREAM && Object.keys(globalFilters).length > 0) {
      fetchStats();
    }
  }, [fetchStats, globalFilters, SHOW_LIVE_ISSUE_STREAM]);

  return (
    <StyledCard sx={{ display: "flex", flexDirection: "column" }}>
      <Grid2 container gap={1} mb={SHOW_LIVE_ISSUE_STREAM ? 2.5 : 1}>
        <Box>
          <Lottie options={LOTTIE_OPTIONS} height={20} width={20} />
        </Box>
        <Typography variant="fw500s16lh20c000000">Live Issue Stream</Typography>
      </Grid2>

      {SHOW_LIVE_ISSUE_STREAM ? (
        <Grid2 container flexDirection="column" gap={1} sx={{ flex: 1 }}>
          {loading
            ? [0, 1, 2, 3].map((i) => (
                <Box key={i} sx={{ flex: 1, borderLeft: "2px solid #E4E4E4", padding: "16px 20px", display: "flex", flexDirection: "column", justifyContent: "space-between", gap: 1, backgroundColor: "#F9F9F9" }}>
                  <Box>
                    <Skeleton variant="text" width={80} height={20} />
                    <Skeleton variant="text" width={48} height={32} />
                  </Box>
                  <Skeleton variant="text" width={120} height={16} />
                </Box>
              ))
            : alertStats.map((alert) => {
                const style = severityStyles[alert.severity];
                return (
                  <StyledAlertBox key={`${alert.severity}-${alert.label}`} severity={alert.severity} sx={{ flex: 1, justifyContent: "space-between" }}>
                    <Box>
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="fw600s14c000000" sx={{ color: "#2E2E2E" }}>{alert.label}</Typography>
                      </Box>
                      <Box sx={{ mb: 0.5 }}>
                        <Typography variant="fw500s20c000000" sx={{ color: style.text, fontSize: "24px", lineHeight: "28px" }}>
                          {alert.totalCount}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ mb: 1.5 }}>
                      <Typography variant="fw400s12c494949">
                        {alert.actionCount} {alert.actionCount === 1 ? "vehicle" : "vehicles"} {style.actionText}
                      </Typography>
                    </Box>
                    <Link
                      to={alert.viewUrl}
                      style={{ display: "contents", textDecoration: "none" }}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      <Grid2 container alignItems="center" gap={0.5}>
                        <Typography variant="fw600s12c8D8D8D" sx={{ lineHeight: "20px", color: style.text }}>View</Typography>
                        <ExpandMoreIcon fontSize="medium" sx={{ transform: "rotate(-90deg)", color: style.text }} />
                      </Grid2>
                    </Link>
                  </StyledAlertBox>
                );
              })}
        </Grid2>
      ) : (
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
          <Box sx={{ mb: 2 }}>
            <CustomIcon name="under-construction-icon" />
          </Box>
          <Typography variant="fw400s14c494949" sx={{ lineHeight: "24px" }}>Module in Progress</Typography>
          <Typography variant="fw600s16c000000" sx={{ lineHeight: "24px", color: "#2E2E2E" }}>Coming Soon</Typography>
        </Box>
      )}
    </StyledCard>
  );
};

export default LiveIssueStreamCard;
